/**
 * **Outlet** — daftar, pembuatan, dan setelan MODE JUAL.
 *
 * ⭐ Mode jual tetap dipasang **di kolom tabel**, bukan hanya di dalam laci: ia harus dijawab
 * sebelum outlet dibuka (kolom wajib-isi `outlet.sale_mode`) dan menentukan bentuk layar yang
 * dimuat kasir. Setelan sepenting itu tak boleh perlu dua ketukan untuk dilihat.
 *
 * ⛔ Pada sesi PIN outlet, sakelarnya **tidak dipasang** — bukan dipasang lalu ditolak server.
 *
 * ### ⭐⭐ Yang baru (Imron, 2026-08-03): outlet BISA DIBUAT dari sini
 * Sebelumnya outlet hanya lahir lewat importir. Tenant yang membuka cabang kedua buntu di layar
 * ini. ⚠️ Empat hal yang **wajib dijawab sadar** saat membuatnya — brand, mode jual, zona waktu,
 * dan jam tutup hari — semuanya NOT NULL tanpa default di DDL, dan tiga di antaranya tak punya
 * jawaban benar yang bisa ditebak mesin.
 */
import {
  Alert, Button, Card, Checkbox, Form, Input, InputNumber, Modal, Select, Space, Switch, Tag,
  TimePicker,
  Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  OUTLET_CAPABILITIES, OUTLET_PRESETS, PRESET_SPEC, isCapabilityImplemented,
} from '@osbpos/contract';
import type { OutletPreset } from '@osbpos/contract';

import { api } from '../api/client';
import type {
  Brand, Outlet, OutletCapabilityRow, OutletTax, PriceGroup, Readiness, TaxCode,
} from '../api/client';
import { canWrite } from '../api/session';
import { NAV } from '../layout/nav';
import { navigateTo } from '../layout/navigate';
import { CrudPanel } from '../components/CrudPanel';
import { FormTabs } from '../components/FormTabs';

/**
 * **Preset onboarding** — disaring menurut JENIS USAHA brandnya.
 *
 * ### ⭐⭐ Kenapa preset, bukan menanyakan kapabilitas satu per satu
 * Yang dibaca engine adalah **kapabilitas** (`kitchen_routing`, `barcode_lookup`, …), bukan jenis
 * usaha — `business.ts` melarang keras cabang `if (businessType === 'fnb')`. Tapi menanyakan enam
 * kapabilitas kepada pemilik warteg adalah pertanyaan yang tak bisa ia jawab. ⇒ ia memilih **satu
 * kalimat yang ia kenali** ("warung makan"), dan preset yang menyalakan kapabilitas yang benar.
 *
 * ⚠️ Brand sudah membawa jenis usahanya, jadi daftar ini disaring — pemilik brand F&B tak pernah
 * ditawari "minimarket". Server tetap menolak yang tak cocok (`precheck`); penyaringan di sini
 * supaya orang tak perlu mencoba lalu ditolak.
 *
 * ⭐ Memilih preset **mengisikan mode jual**, tapi tidak menguncinya: resto yang membuka gerai
 * quick service di food court tetap bisa mengubahnya (`PRESET_SPEC.saleMode` = "bawaan preset ini,
 * pemilik tetap boleh mengubahnya").
 */
function PresetPicker({ brand }: { readonly brand: readonly Brand[] }) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const brandId = Form.useWatch('brandId', form) as string | undefined;
  const jenis = brand.find((b) => b.id === brandId)?.business_type;

  const pilihan = OUTLET_PRESETS.filter((p) => PRESET_SPEC[p].businessType === jenis);
  const preset = Form.useWatch('preset', form) as OutletPreset | undefined;

  /*
   * ⛔ Mengisikan mode jual lewat `useWatch` + efek, BUKAN `onChange` pada `<Select>`.
   *
   * `Form.Item` menyuntikkan `onChange`-nya sendiri ke anaknya; `onChange` yang kita pasang
   * MENIMPANYA, dan akibatnya nilai presetnya sendiri tak pernah tercatat ke formulir — hilang
   * diam-diam, tanpa galat, dan yang ketahuan cuma "mode jualnya tidak terisi".
   */
  useEffect(() => {
    if (preset === undefined) return;
    /* ⭐ Mengisikan, bukan mengunci: resto yang membuka gerai di food court tetap bisa mengubahnya. */
    form.setFieldValue('saleMode', PRESET_SPEC[preset].saleMode);
  }, [preset, form]);

  return (
    <Form.Item name="preset" label={t('outlets.preset')} tooltip={t('outlets.presetHint')}>
      <Select
        allowClear
        disabled={jenis === undefined}
        placeholder={jenis === undefined ? t('outlets.presetPickBrand') : undefined}
        options={pilihan.map((p) => ({
          value: p,
          label: `${t(`preset.${p}`)} — ${PRESET_SPEC[p].saleMode}`,
        }))}
      />
    </Form.Item>
  );
}

/** ⛔ Enum kontrak — bukan teks bebas. Zona karangan = `business_date` yang salah sepanjang tahun. */
const TIMEZONES = ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura'] as const;

/** Setelan outlet, dikunci `outlet_id`. ⚠️ Barisnya boleh TAK ADA — itu keadaan yang sah. */
type SetelanPerOutlet = Readonly<Record<string, Record<string, unknown>>>;

async function muatSetelan(): Promise<SetelanPerOutlet> {
  const rows = await api.outletSettings();
  const map: Record<string, Record<string, unknown>> = {};
  for (const r of rows) map[String(r['outlet_id'])] = r;
  return map;
}

/**
 * Nilai form untuk tab Dapur.
 *
 * ⚠️ `kdsOverride` diturunkan dari ADA-TIDAKNYA nilai, bukan dari kolom boolean tersendiri. Boolean
 * terpisah berarti dua sumber kebenaran yang bisa berselisih — "override menyala tapi nilainya
 * kosong" adalah keadaan yang tak berarti apa-apa, dan pasti muncul cepat atau lambat.
 */
function kdsForm(setelan: Record<string, unknown> | undefined): Record<string, unknown> {
  const warn = setelan?.['kds_warn_seconds'];
  const late = setelan?.['kds_late_seconds'];
  const ada = warn !== null && warn !== undefined;
  return {
    kdsOverride: ada,
    kdsWarnMinutes: ada ? Math.round(Number(warn) / 60) : undefined,
    kdsLateMinutes: ada ? Math.round(Number(late) / 60) : undefined,
    /*
     * ⚠️ Bawaannya **false** — dan itu `?? false`, bukan `!== false`. Outlet yang belum punya baris
     * setelan sama sekali harus terbaca MELARANG, sama seperti yang server putuskan. Layar yang
     * menampilkan "boleh" untuk keadaan yang sebenarnya "tak boleh" membuat pemilik mengira ia
     * sudah menyalakan sesuatu yang tak pernah menyala.
     */
    allowReopenAfterCashTender: setelan?.['allow_reopen_after_cash_tender'] === true,
  };
}


/**
 * **Timpaan kebijakan milik outlet** (0046).
 *
 * ⛔⛔ `null` berarti **IKUT BRAND**, bukan "nol". Karena itu tiap baris punya saklar
 * *ikut brand / setel sendiri* tersendiri: tanpa saklar, satu-satunya cara "kembali ikut brand"
 * adalah mengosongkan kotaknya — dan kotak angka yang dikosongkan terkirim sebagai `0`, yang
 * artinya "toleransi selisih kas NOL", persis kebalikan dari yang dimaksud.
 *
 * ⚠️ Polanya sama dengan `kdsOverride`: keadaan "menimpa" DITURUNKAN dari ada-tidaknya nilai,
 * bukan dari kolom boolean tersendiri. Boolean terpisah = dua sumber kebenaran yang bisa
 * berselisih, dan "override menyala tapi nilainya kosong" adalah keadaan yang tak berarti apa-apa.
 */
const POLICY_FIELDS = Object.freeze([
  ['cashVarianceThreshold', 'cash_variance_threshold'],
  ['drawerOpenReviewThreshold', 'drawer_open_review_threshold'],
  ['reprintWindowMinutes', 'reprint_window_minutes'],
  ['allowReopenAfterCashTender', 'allow_reopen_after_cash_tender'],
  ['removeSentLineWindowMinutes', 'remove_sent_line_window_minutes'],
  ['printZeroPriceModifiers', 'print_zero_price_modifiers'],
  ['guestCallCooldownSeconds', 'guest_call_cooldown_seconds'],
  ['guestCallSpamThreshold', 'guest_call_spam_threshold'],
  ['guestCallSuspendMinutes', 'guest_call_suspend_minutes'],
  ['guestCallRequiresOpenShift', 'guest_call_requires_open_shift'],
] as const);

function policyForm(setelan: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [form, kolom] of POLICY_FIELDS) {
    const v = setelan?.[kolom];
    const ada = v !== null && v !== undefined;
    out[`ov_${form}`] = ada;
    out[form] = ada ? (typeof v === 'string' ? Number(v) : v) : undefined;
  }
  return out;
}

/** ⚠️ Yang TAK ditimpa dikirim `null` EKSPLISIT — itulah yang mencabutnya kembali ke brand. */
function policyBody(v: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [form] of POLICY_FIELDS) {
    if (v[`ov_${form}`] !== true) { out[form] = null; continue; }
    const nilai = v[form];
    out[form] =
      form === 'cashVarianceThreshold'
        ? `${Number(nilai ?? 0).toFixed(4)}`
        : typeof nilai === 'boolean' ? nilai : Number(nilai);
  }
  return out;
}

/**
 * Uang untuk DIBACA. ⚠️ Nilainya tetap string berskala 4 di kawat (P1); yang diformat hanya
 * tampilannya, dan tak ada satu pun keputusan yang diambil dari hasil format ini.
 */
function rp(v: string): string {
  return `Rp ${Number(v).toLocaleString('id-ID')}`;
}

/**
 * ⚠️ `0` berarti **MATI**, dan itu ditulis dengan kata.
 *
 * ⛔ "0 menit" terbaca sebagai batas waktu selebar nol — yang kebetulan artinya sama, tapi lewat
 * jalan pikir yang salah, dan jalan pikir yang salah akan meleset di angka berikutnya.
 */
function menit(v: number, t: TFunction): string {
  return v === 0 ? t('policy.removeSentOff') : t('policy.windowMeans', { n: v });
}

/** Nilai brand yang sedang berlaku, untuk ditampilkan di sebelah saklar "ikut brand". */
function ya(v: boolean | undefined, t: TFunction): string {
  return v === true ? t('common.on') : t('common.off');
}

/**
 * Satu baris kebijakan: saklar *ikut brand* + kolomnya. Kolomnya MATI selama ikut brand — kotak
 * hidup yang nilainya tak dipakai adalah kotak yang akan diisi lalu dikira berlaku.
 */
function BarisPolicy(p: {
  readonly name: string;
  readonly label: string;
  readonly tooltip: string;
  readonly testId: string;
  readonly brand: ReactNode;
  readonly children: ReactNode;
}) {
  /* ⚠️ Kamusnya diambil DI SINI — komponen ini hidup di luar `Outlets()`. */
  const { t } = useTranslation();
  return (
    <Form.Item noStyle shouldUpdate>
      {(form) => {
        const on = form.getFieldValue(`ov_${p.name}`) === true;
        return (
          <Space align="start" wrap style={{ marginBottom: 8 }}>
            <Form.Item name={`ov_${p.name}`} label={p.label} tooltip={p.tooltip}
                       valuePropName="checked">
              <Switch data-testid={`ov-${p.testId}`}
                      checkedChildren={t('policy.override')} unCheckedChildren={t('policy.inherit')} />
            </Form.Item>
            {on ? p.children : (
              <Form.Item label=" ">
                <Typography.Text type="secondary" data-testid={`brand-${p.testId}`}>
                  {p.brand}
                </Typography.Text>
              </Form.Item>
            )}
          </Space>
        );
      }}
    </Form.Item>
  );
}

/**
 * ⭐⭐ **Di layar mana sebuah gerbang kesiapan diperbaiki.**
 *
 * ⛔ "Yang kurang" dan "tempat memperbaikinya" hidup di dua layar berbeda. Menyebut namanya tanpa
 * mengantar ke sana menyisakan pekerjaan mencari — dan yang paling mungkin terjadi bukan
 * pencarian, melainkan penundaan.
 *
 * ⚠️ Kunci kiri = `ReadinessCheck.key` dari SERVER; nilai kanan = `NAV[].items[].key`. Gerbang yang
 * belum terpetakan sengaja **tidak** dikarang tujuannya: tautan yang mengantar ke layar yang salah
 * lebih buruk daripada tak ada tautan, karena orang akan mencari di tempat yang keliru lalu
 * menyimpulkan sistemnya yang rusak.
 */
/**
 * Nama layar untuk tombolnya — **diturunkan dari `NAV`**, bukan ditulis ulang.
 *
 * ⛔ Daftar kedua akan menyimpang: menu berganti nama, tombol pintasnya tetap menyebut yang lama,
 * dan orang mencari menu yang sudah tak ada.
 */
const NAMA_LAYAR: Readonly<Record<string, string>> = Object.fromEntries(
  NAV.flatMap((g) => g.items.map((i) => [i.key, i.label])),
);

const LAYAR_PERBAIKAN: Readonly<Record<string, string>> = {
  /* Zona & jam pergantian hari disunting di laci outlet ini sendiri. */
  day_cutover_time: 'outlets',
  timezone: 'outlets',
  outlet_tax: 'taxes',
  product_tax_group: 'taxes',
  payment_method: 'payments',
  /* Harga hidup di laci produk; daftar produknya pintu masuknya. */
  product_price: 'products',
  /* Perangkat & meja sama-sama di layar Lantai. */
  cashier_device: 'floor',
  dining_table: 'floor',
  /* Stasiun cetak & rute produk sama-sama di layar Routing. */
  print_station: 'routing',
  product_route: 'routing',
  staff: 'staff',
  /* ⭐ Izin `payment:take` ditempelkan lewat pemetaan jabatan → peran POS. */
  payment_permission: 'roles',
};

/**
 * Pemilih grup harga — **disaring menurut BRAND outletnya**.
 *
 * ### ⛔⛔ Kenapa ini komponen tersendiri
 * Saringannya dulu berbunyi `editing === null || g.brand_id === editing.brand_id` — artinya ia
 * hanya bekerja saat MENYUNTING. Tepat saat outlet **BARU dibuat**, seluruh grup dari seluruh brand
 * muncul; dan itu yang dilaporkan Imron (2026-08-06): outlet baru di brand *Seribu Rasa* bisa
 * memilih grup harga *PB* milik Penang Bistro.
 *
 * ⇒ Saat membuat, brandnya belum ada di baris mana pun — ia ada di FORM. Membacanya menuntut
 *   `Form.useWatch`, dan hook menuntut komponen sungguhan (pola yang sama dengan `FieldPromo`).
 *
 * ⚠️ Ini lapisan KEDUA. Yang menegakkan sesungguhnya FK gabungan `(price_group_id, brand_id)` di
 * migrasi 0053 — layar yang menyaring tanpa DB yang menolak cuma menyembunyikan pintunya, dan
 * `precheck` aplikasi pun bocor lewat jalur UPDATE.
 */
function PilihGrupHarga(
  { grup, editing }: { readonly grup: readonly PriceGroup[]; readonly editing: Outlet | null },
) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const brandDipilih = Form.useWatch('brandId', form) as string | undefined;
  const brandId = brandDipilih ?? editing?.brand_id;

  const pilihan = grup.filter((g) => brandId === undefined || g.brand_id === brandId);

  return (
    <Form.Item name="priceGroupId" label={t('priceGroup.title')} tooltip={t('priceGroup.switchHint')}>
      <Select
        allowClear
        data-testid="outlet-grup-harga"
        placeholder={brandId === undefined ? t('structure.pickBrand') : t('priceGroup.none')}
        /* ⛔ Tanpa brand terpilih, TAK ADA pilihan sama sekali — daftar lintas-brand yang muncul
           "sementara" adalah persis yang menyesatkan orang. */
        disabled={brandId === undefined}
        options={pilihan.map((g) => ({ value: g.id, label: `${g.code} · ${g.name}` }))}
      />
    </Form.Item>
  );
}

export function Outlets() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [brand, setBrand] = useState<readonly Brand[]>([]);
  /*
   * ⭐ Nilai BRAND ditampilkan di sebelah tiap saklar yang masih "ikut brand".
   *
   * ⛔ Tanpa itu, "ikut brand" adalah janji kosong: pemilik tak tahu apa yang sedang diikutinya,
   * dan satu-satunya cara mencari tahu adalah membuka layar Brand di tab lain.
   */
  const brandDari = useCallback(
    (o: Outlet | null) => (o === null ? undefined : brand.find((b) => b.id === o.brand_id)),
    [brand],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  /** Daftar outlet apa adanya dari `muat` — sumber untuk pemeriksaan kesiapan. */
  const [daftar, setDaftar] = useState<readonly Outlet[]>([]);

  const [grup, setGrup] = useState<readonly PriceGroup[]>([]);
  const [setelan, setSetelan] = useState<SetelanPerOutlet>({});
  useEffect(() => { void muatSetelan().then(setSetelan).catch(() => undefined); }, []);

  /**
   * Menyimpan baris `outlet_settings` — ambang KDS **dan** kebijakan buka-kembali bill tunai.
   *
   * ⛔ Mencabut ambang KDS = menulis NULL berdua, bukan menghapus barisnya: baris setelan memuat
   * hal lain juga (prefix nomor, ambang selisih kas, kebijakan di bawah), dan menghapusnya akan
   * ikut menghanyutkan semuanya.
   *
   * ⚠️ Keduanya disimpan lewat SATU permintaan. Dulu fungsi ini cuma mengurus KDS, dan menambahkan
   * kebijakan sebagai panggilan kedua berarti satu bisa berhasil sementara satunya gagal —
   * meninggalkan layar yang menampilkan dua setelan padahal cuma satu yang tersimpan.
   */
  const simpanSetelan = useCallback(async (outletId: string, v: Record<string, unknown>) => {
    const kdsAktif = v['kdsOverride'] === true;
    const kebijakan = policyBody(v);
    const body = {
      ...(kdsAktif
        ? {
            kdsWarnSeconds: Number(v['kdsWarnMinutes'] ?? 0) * 60,
            kdsLateSeconds: Number(v['kdsLateMinutes'] ?? 0) * 60,
          }
        : { kdsWarnSeconds: null, kdsLateSeconds: null }),
      /*
       * ⭐⭐⭐ **Deklarasi outlet bebas pajak.** Gerbang `outlet_tax` sendiri menyebutnya sebagai
       * satu-satunya jalan keluar yang sah — *"ATAU `outlet_settings.tax_exempt_declared = true`
       * bila outlet ini memang tanpa pajak"*. Selama tak ada layarnya, outlet yang memang tak
       * memungut pajak **tak akan pernah lolos kesiapan**: jalan buntu tanpa obat.
       *
       * ⛔ Dikirim `false` EKSPLISIT saat tak dicentang, bukan dihilangkan — menghilangkannya
       * berarti "jangan ubah", dan deklarasi yang dicabut orang akan tetap menempel.
       */
      taxExemptDeclared: v['taxExemptDeclared'] === true,
      ...kebijakan,
    };
    const row = setelan[outletId];
    if (row === undefined) {
      /*
       * ⚠️ Outlet yang belum pernah disetel apa pun belum punya barisnya — dan tanpa baris,
       * SEMUANYA sudah bernilai bawaannya (ikut brand). Barisnya baru dibuat kalau ada yang
       * benar-benar menyimpang dari itu.
       *
       * ⛔ Diperiksa dari `kebijakan`, bukan dari daftar yang ditulis tangan: menambah kebijakan
       * baru lalu lupa memperbaruinya di sini berarti timpaan pertama sebuah outlet diam-diam
       * tak pernah tersimpan.
       */
      /*
       * ⛔⛔ **`taxExemptDeclared` WAJIB ikut dihitung di sini.** Penjaga ini ada supaya outlet yang
       * tak menimpa apa pun tak melahirkan baris setelan kosong — masuk akal. Tapi ia menilai
       * "tak ada yang perlu disimpan" dari daftar kebijakan SAJA, jadi outlet yang cuma menyatakan
       * dirinya bebas pajak pulang dengan tangan kosong: layar bilang tersimpan, servernya tak
       * pernah menerima apa pun, dan gerbang kesiapan tetap menolak dengan alasan yang sama.
       *
       * ⚠️ Persis kelas bug yang ditemukan berkali-kali hari ini: setelan yang bisa diketik tapi
       * tak pernah sampai.
       */
      const adaTimpaan = Object.values(kebijakan).some((x) => x !== null);
      if (!kdsAktif && !adaTimpaan && v['taxExemptDeclared'] !== true) return;
      await api.createOutletSettings({ outletId, ...body });
      return;
    }
    await api.updateMaster('outlet-settings', String(row['id']), body);
  }, [setelan]);

  useEffect(() => { void api.brands(true).then(setBrand).catch(() => undefined); }, []);
  /* ⚠️ Grup harga MILIK BRAND — tapi laci ini melayani semua brand, jadi daftarnya diambil utuh
     lalu disaring ke brand outletnya saat laci dibuka. */
  useEffect(() => { void api.priceGroups().then(setGrup).catch(() => undefined); }, []);

  /* ⭐⭐ Kapabilitas: INI yang dibaca engine. Preset cuma menyetel nilai awalnya. */
  const [kapabilitas, setKapabilitas] = useState<readonly OutletCapabilityRow[]>([]);
  const muatKapabilitas = useCallback(() => {
    void api.outletCapabilities().then(setKapabilitas).catch(() => undefined);
  }, []);
  useEffect(() => { muatKapabilitas(); }, [muatKapabilitas]);

  const ubahKapabilitas = (outletId: string, cap: string, on: boolean) => {
    const ada = kapabilitas.find((k) => k.outlet_id === outletId && k.capability === cap);
    void (async () => {
      try {
        if (on && ada === undefined) await api.addCapability({ outletId, capability: cap });
        else if (!on && ada !== undefined) await api.removeCapability(ada.id);
        muatKapabilitas();
      } catch (e) {
        void message.error(e instanceof Error ? e.message : String(e));
      }
    })();
  };

  /* ⭐ Pajak yang berlaku di outlet — arah baca sebaliknya dari layar Pajak. */
  const [kodePajak, setKodePajak] = useState<readonly TaxCode[]>([]);
  const [tugasPajak, setTugasPajak] = useState<readonly OutletTax[]>([]);
  useEffect(() => { void api.taxCodes().then(setKodePajak).catch(() => undefined); }, []);
  const muatTugasPajak = useCallback(() => {
    void api.outletTaxes().then(setTugasPajak).catch(() => undefined);
  }, []);
  useEffect(() => { muatTugasPajak(); }, [muatTugasPajak]);

  const ubahPajak = (outletId: string, taxCodeId: string, on: boolean) => {
    const ada = tugasPajak.find((x) => x.outlet_id === outletId && x.tax_code_id === taxCodeId);
    void (async () => {
      try {
        if (on && ada === undefined) {
          await api.assignOutletTax({
            outletId, taxCodeId, validFrom: dayjs().format('YYYY-MM-DD'),
          });
        } else if (!on && ada !== undefined) {
          await api.unassignOutletTax(ada.id);
        }
        muatTugasPajak();
      } catch (e) {
        void message.error(e instanceof Error ? e.message : String(e));
      }
    })();
  };

  const muat = useCallback(async (): Promise<readonly Outlet[]> => {
    try {
      const r = await api.outlets(undefined, true);
      setError(null);
      /* ⭐ Disimpan juga di state: kolom KESIAPAN perlu tahu outlet apa saja yang ada, dan
         memuat daftarnya dua kali cuma menambah permintaan tanpa menambah jawaban. */
      setDaftar(r);
      return r;
    } catch (e) {
      /* ⚠️ Gagal muat ⇒ daftar KOSONG dengan pesan, bukan daftar lama yang basi: daftar outlet
         yang basi menuntun ke keputusan atas outlet yang mungkin sudah tak ada. */
      setError(e instanceof Error ? e.message : String(e));
      return [];
    }
  }, []);

  const ubahMode = async (o: Outlet, mode: string) => {
    setBusy(o.id);
    try {
      await api.setSaleMode(o.id, mode);
      setTick((n) => n + 1);
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const namaBrand = new Map(brand.map((b) => [b.id, b.code]));

  /*
   * ⭐⭐⭐ **Gerbang kesiapan, TERLIHAT di daftar outlet.**
   *
   * ⛔ Sebelum ini daftar periksa itu hanya hidup di dalam `openShift`: outlet yang belum lengkap
   * baru ketahuan saat kasir mencoba membuka sesi — **di pagi hari buka**, di depan tamu, oleh
   * orang yang tak punya akses memperbaikinya. Yang tahu isinya cuma yang mau memanggil endpoint
   * dengan `curl`.
   *
   * ⭐ Angkanya DATANG DARI SERVER apa adanya (`checkOutletReadiness`), tak pernah dihitung ulang
   * di layar — aturan yang sama dengan layar Routing. Layar yang punya rumusnya sendiri akan
   * menyimpang dari gerbang yang sesungguhnya menolak, dan hasilnya pesan paling membingungkan yang
   * bisa dihasilkan sistem ini: *"layar bilang beres, kasir tetap ditolak"*.
   *
   * ⚠️ Dimuat untuk SETIAP outlet, bukan hanya yang sedang dibuka: "outlet mana yang belum siap"
   * adalah pertanyaan yang muncul lebih dulu daripada "apa yang kurang di outlet ini".
   */
  const [siap, setSiap] = useState<Record<string, Readiness>>({});
  useEffect(() => {
    let alive = true;
    void Promise.all(
      daftar.filter((o) => o.is_active).map(async (o) => {
        try {
          return [o.code, await api.readiness(o.code)] as const;
        } catch {
          /* ⚠️ Gagal memeriksa TIDAK menghalangi layarnya — outletnya tetap bisa disunting. */
          return null;
        }
      }),
    ).then((hasil) => {
      if (!alive) return;
      setSiap(Object.fromEntries(hasil.filter((x) => x !== null)));
    });
    return () => { alive = false; };
    /* ⚠️ `tick` ikut: sesudah orang memperbaiki sesuatu, angkanya WAJIB turun sendiri —
       angka yang tak ikut berubah membuat orang mengira perbaikannya tak tersimpan. */
  }, [daftar, tick]);

  /** Rincian per-outlet: yang GAGAL di atas, yang sudah lolos di bawah. */
  const bukaRincian = useCallback((o: Outlet) => {
    const r = siap[o.code];
    if (r === undefined) return;
    const gagal = r.checks.filter((c) => !c.ok);
    const lolos = r.checks.filter((c) => c.ok);
    Modal.info({
      title: `${t('outlets.readiness')} — ${o.code}`,
      width: 720,
      content: (
        <div data-testid="kesiapan-rincian">
          {gagal.length === 0 ? (
            <p>{t('outlets.readinessOk')}</p>
          ) : (
            <ul>
              {gagal.map((c) => (
                <li key={c.key} style={{ marginBottom: 12 }}>
                  <b>⛔ {c.key}</b>
                  {/* ⭐ Teks `detail` datang dari SERVER — di situlah alasan & cara memperbaikinya
                      sudah ditulis, dan menyalinnya ke layar berarti dua versi yang menyimpang. */}
                  <div style={{ opacity: 0.85 }}>{c.detail}</div>
                  {LAYAR_PERBAIKAN[c.key] !== undefined && (
                    <Button
                      size="small"
                      type="link"
                      style={{ paddingLeft: 0 }}
                      data-testid={`kesiapan-buka-${c.key}`}
                      onClick={() => {
                        /* ⛔ Dialognya DITUTUP dulu: modal yang menggantung di atas layar tujuan
                           membuat orang mengira klik-nya tak berhasil, lalu menekannya lagi. */
                        Modal.destroyAll();
                        /* ⭐ Outletnya ikut: layar tujuan membuka dengan outlet YANG INI terpilih,
                           bukan kosong lalu menyuruh orang menebak tadi outlet mana. */
                        navigateTo(LAYAR_PERBAIKAN[c.key] as string, o.id);
                      }}
                    >
                      {t('outlets.readinessFix', {
                        layar: t(NAMA_LAYAR[LAYAR_PERBAIKAN[c.key] as string] ?? ''),
                      })}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {lolos.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary>{t('outlets.readinessPassed', { n: lolos.length })}</summary>
              <ul>{lolos.map((c) => <li key={c.key}>✓ {c.key}</li>)}</ul>
            </details>
          )}
        </div>
      ),
    });
  }, [siap, t]);

  const kolom: ColumnsType<Outlet> = [
    {
      title: t('outlets.code'),
      dataIndex: 'code',
      width: 140,
      render: (v: string) => <b data-testid={`outlet-${v}`}>{v}</b>,
    },
    { title: t('outlets.name'), dataIndex: 'name' },
    {
      /* ⭐ Kolom brand: satu tenant boleh punya beberapa brand, dan outlet yang salah brand
         mewarisi seluruh menu yang salah. Ia harus terbaca tanpa membuka apa pun. */
      title: t('structure.brand'),
      dataIndex: 'brand_id',
      width: 120,
      render: (v: string) => <Tag>{namaBrand.get(v) ?? '—'}</Tag>,
    },
    {
      title: t('outlets.saleMode'),
      dataIndex: 'sale_mode',
      width: 290,
      render: (v: string, o: Outlet) =>
        boleh ? (
          /*
           * ⚠️ `data-mode` dipasang di pembungkus: nilai enum harus bisa diperiksa uji tanpa
           * membuka daftar pilihan — dan NILAI-nya tak pernah ikut diterjemahkan.
           *
           * ⛔ `stopPropagation`: barisnya juga membuka laci sunting. Tanpa ini, mengganti mode
           * jual sekaligus membuka formulir — satu ketukan, dua maksud.
           */
          <span data-testid={`mode-${o.code}`} data-mode={v} onClick={(e) => { e.stopPropagation(); }}>
            <Select
              style={{ width: 260 }}
              value={v}
              loading={busy === o.id}
              onChange={(m: string) => void ubahMode(o, m)}
              options={[
                { value: 'table_service', label: t('outlets.tableService') },
                { value: 'quick_service', label: t('outlets.quickService') },
              ]}
            />
          </span>
        ) : (
          <span data-testid={`mode-teks-${o.code}`} data-mode={v}>
            {v === 'table_service' ? t('outlets.tableServiceShort') : t('outlets.quickServiceShort')}
          </span>
        ),
    },
    { title: t('outlets.cutover'), dataIndex: 'day_cutover_time', width: 110 },
    {
      title: t('outlets.readiness'),
      width: 150,
      render: (_: unknown, o: Outlet) => {
        const r = siap[o.code];
        if (r === undefined) return <Tag>—</Tag>;
        /*
         * ⚠️ Yang gagal menyebut JUMLAHNYA, bukan cuma "belum siap": angka membuat orang tahu ini
         * pekerjaan lima menit atau setengah hari, dan itu menentukan apakah ia dikerjakan sekarang.
         */
        return (
          <Tag
            color={r.ready ? 'green' : 'red'}
            style={{ cursor: 'pointer' }}
            data-testid={`kesiapan-${o.code}`}
            onClick={() => { bukaRincian(o); }}
          >
            {r.ready
              ? t('outlets.readinessReady')
              : t('outlets.readinessBlocked', { n: r.blockers.length })}
          </Tag>
        );
      },
    },
    {
      /*
       * ⭐⭐ Kapan outlet ini terakhir DIKIRIMI data. ⚠️ Ini bukan "kasirnya sudah menariknya" —
       * yang itu ada per PERANGKAT di layar Lantai. Menyatukan keduanya di satu kolom membuat
       * admin mengira pekerjaannya selesai begitu ia menekan Send Data.
       */
      title: t('outlets.sentAt'),
      dataIndex: 'catalog_published_at',
      width: 150,
      render: (v: string | null) =>
        v == null
          ? <Tag data-testid="belum-kirim">{t('outlets.neverSent')}</Tag>
          : <span>{v.slice(0, 16).replace('T', ' ')}</span>,
    },
    {
      title: t('outlets.status'),
      dataIndex: 'is_active',
      width: 96,
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'}>{v ? t('outlets.active') : t('outlets.inactive')}</Tag>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="outlets">
      {!boleh && (
        <Alert
          type="info"
          showIcon
          data-testid="hanya-lihat"
          title={<Trans i18nKey="outlets.readOnly" components={{ 1: <b /> }} />}
        />
      )}
      {error !== null && <Alert type="error" showIcon data-testid="outlet-error" title={error} />}

      <SendData brands={brand} onSent={() => { setTick((n) => n + 1); }} />

      <BatasLuring boleh={boleh} />

      <CrudPanel<Outlet>
        testId="outlet"
        historyPath="outlets"
        title={t('outlets.title')}
        subtitle={t('outlets.subtitle')}
        canWrite={boleh}
        newLabel={t('outlets.new')}
        columns={kolom}
        fetchList={muat}
        reloadToken={tick}
        searchIn={(o) => `${o.code} ${o.name}`}
        emptyText={<span data-testid="outlet-kosong">{t('outlets.empty')}</span>}
        formWidth={720}
        blockedReason={brand.length === 0 ? t('outlets.needBrand') : null}
        renderFields={(_f, editing) => (
          <FormTabs
            testId="outlet"
            items={[
              {
                key: 'identitas',
                label: t('outlets.tabIdentity'),
                names: ['code', 'brandId', 'preset', 'name'],
                children: (
                  <>
                    {editing === null ? (
                      <>
                        <Form.Item
                          name="code"
                          label={t('outlets.code')}
                          rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                          tooltip={t('structure.codeFixed')}
                        >
                          <Input maxLength={40} data-testid="outlet-kode" />
                        </Form.Item>
                        {/* ⛔ Brand WAJIB & beku sesudahnya: memindah outlet antar-brand berarti
                            seluruh menu, harga, dan promonya berganti — itu outlet baru. */}
                        <Form.Item
                          name="brandId"
                          label={t('structure.brand')}
                          rules={[{ required: true }]}
                          tooltip={t('outlets.brandFixed')}
                        >
                          <Select options={brand.map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` }))} />
                        </Form.Item>
                        <PresetPicker brand={brand} />
                      </>
                    ) : (
                      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('outlets.frozen')} />
                    )}
                    <Form.Item name="name" label={t('outlets.name')} rules={[{ required: true, min: 2 }]}>
                      <Input maxLength={120} data-testid="outlet-nama" />
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'dapur',
                label: t('outlets.tabKitchen'),
                names: ['kdsOverride', 'kdsWarnMinutes', 'kdsLateMinutes'],
                children: (
                  <>
                    {/*
                      ⭐⭐ Ambang usia tiket dapur dimiliki BRAND. Di sini hanya PENIMPAAN, dan
                      keadaannya dinyatakan terang-terangan — kalau tidak, manajer tak bisa tahu
                      apakah angka yang dilihatnya miliknya sendiri atau warisan brand, dan karena
                      itu tak tahu apakah mengubah brand akan berpengaruh ke outlet ini.
                    */}
                    <Form.Item name="kdsOverride" label={t('outlets.kdsOverride')} valuePropName="checked"
                               tooltip={t('outlets.kdsOverrideHint')}>
                      <Switch
                        data-testid="outlet-kds-override"
                        checkedChildren={t('common.on')}
                        unCheckedChildren={t('common.off')}
                      />
                    </Form.Item>

                    {/*
                      ⛔ Keduanya muncul & hilang BERSAMAAN — mengisi satu saja bisa menghasilkan
                      pasangan terbalik terhadap nilai brand (kuning 20 mnt vs merah brand 15 mnt),
                      dan kuning tak akan pernah terlihat. Ditegakkan juga CHECK di DB (0043).
                    */}
                    <Form.Item noStyle shouldUpdate>
                      {(f) => (f.getFieldValue('kdsOverride') === true ? (
                        <Space align="start">
                          <Form.Item name="kdsWarnMinutes" label={t('brands.kdsWarn')}
                                     rules={[{ required: true }]}>
                            <InputNumber min={1} max={240} data-testid="outlet-kds-warn" />
                          </Form.Item>
                          <Form.Item name="kdsLateMinutes" label={t('brands.kdsLate')}
                                     rules={[{ required: true }]}>
                            <InputNumber min={2} max={480} data-testid="outlet-kds-late" />
                          </Form.Item>
                        </Space>
                      ) : (
                        <Alert
                          type="info"
                          showIcon
                          data-testid="outlet-kds-inherit"
                          title={t('outlets.kdsInherit')}
                        />
                      ))}
                    </Form.Item>
                  </>
                ),
              },
              {
                key: 'operasi',
                label: t('outlets.tabOps'),
                names: ['saleMode', 'timezone', 'cutover'],
                children: (
                  <>
                    {/* ⭐⭐ Mode jual tanpa default: default diam-diam berarti resto yang lupa
                        menyetelnya membuka hari pertamanya sebagai quick service. */}
                    <Form.Item
                      name="saleMode"
                      label={t('outlets.saleMode')}
                      rules={[{ required: true }]}
                      tooltip={t('outlets.saleModeHint')}
                    >
                      <Select
                        options={[
                          { value: 'table_service', label: t('outlets.tableService') },
                          { value: 'quick_service', label: t('outlets.quickService') },
                        ]}
                      />
                    </Form.Item>

                    <Space align="start">
                      <Form.Item name="timezone" label={t('outlets.timezone')} initialValue="Asia/Jakarta">
                        <Select style={{ width: 220 }} options={TIMEZONES.map((v) => ({ value: v, label: v }))} />
                      </Form.Item>
                      {/* ⭐⭐ Jam tutup hari = akar `business_date`. Resto tutup 02:00 ⇒ transaksi
                          01:30 masuk hari SEBELUMNYA; salah setel menggeser seluruh laporan harian. */}
                      <Form.Item
                        name="cutover"
                        label={t('outlets.cutover')}
                        rules={[{ required: true }]}
                        initialValue={dayjs('05:00', 'HH:mm')}
                      >
                        <span data-testid="outlet-cutover">
                          <TimePicker format="HH:mm" style={{ width: 150 }} />
                        </span>
                      </Form.Item>
                    </Space>
                    <Alert type="warning" showIcon title={t('outlets.cutoverHint')} />

                    {/*
                      * ⭐⭐⭐ **Kapabilitas — yang SESUNGGUHNYA dibaca engine.**
                      *
                      * `needsKitchenSend`, `itemLookupMode`, `modeStok` semuanya membacanya; preset
                      * hanya menyetel nilai AWALnya, dan `business_type` tak pernah boleh jadi
                      * cabang kode (aturan keras `business.ts`).
                      *
                      * ⛔ Sebelum ini tak ada satu pun jalan mengubahnya sesudah outlet lahir —
                      * kecuali SQL. Preset yang jadi sumber kebenaran runtime mengunci tenant pada
                      * keputusan hari pertamanya.
                      */}
                    {editing !== null && (
                      <>
                        <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
                          {t('outlets.capabilities')}
                        </Typography.Text>
                        <Alert type="warning" showIcon style={{ margin: '8px 0' }}
                               title={t('outlets.capabilityHint')} />
                        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                          {OUTLET_CAPABILITIES.map((c) => (
                            <Checkbox
                              key={c}
                              checked={kapabilitas.some(
                                (k) => k.outlet_id === editing.id && k.capability === c,
                              )}
                              disabled={!boleh}
                              data-testid={`kapabilitas-${c}`}
                              onChange={(e) => { ubahKapabilitas(editing.id, c, e.target.checked); }}
                            >
                              {c} <Typography.Text type="secondary">— {t(`capability.${c}`)}</Typography.Text>
                              {/*
                                ⛔⛔ **Kapabilitas yang belum dibaca kode apa pun DIKATAKAN.**
                                Audit 2026-08-23: 5 dari 7 disetel manajer lalu tak berakibat apa
                                pun — penyakit yang sama dengan `promo.requires_approval` sebelum
                                K23. Setelan yang berbohong lebih buruk daripada setelan yang tak
                                ada: yang menyalakannya berhenti mencari sebab lain saat hasilnya
                                tak muncul.
                                ⚠️ Ditandai, BUKAN disembunyikan — yang disembunyikan tak bisa
                                ditanyakan.
                              */}
                              {!isCapabilityImplemented(c) && (
                                <Tag color="default" data-testid={`kapabilitas-belum-${c}`}>
                                  {t('outlets.capabilityNotYet')}
                                </Tag>
                              )}
                            </Checkbox>
                          ))}
                        </Space>
                      </>
                    )}
                  </>
                ),
              },
              {
                key: 'kebijakan',
                label: t('policy.tab'),
                names: POLICY_FIELDS.flatMap(([f]) => [f, `ov_${f}`]),
                children: (
                  <>
                    {/*
                      ⭐⭐⭐ TIMPAAN, bukan setelan tersendiri. Yang dibiarkan "ikut brand" akan
                      IKUT BERUBAH saat kebijakan brand diubah — dan itu memang gunanya: satu
                      keputusan di brand berlaku ke 47 outlet tanpa 47 kali penyuntingan.
                      ⚠️ Yang ditimpa di sini BERHENTI mengikuti brand, selamanya sampai dicabut.
                    */}
                    <Alert type="info" showIcon style={{ marginBottom: 16 }}
                           title={t('policy.outletHint')} data-testid="outlet-kebijakan-catatan" />

                    {editing === null ? (
                      /* ⚠️ Sama seperti pajak & kapabilitas: timpaan butuh outlet yang sudah ada. */
                      <Alert type="info" showIcon title={t('outlets.taxAfterSave')}
                             data-testid="outlet-kebijakan-nanti" />
                    ) : (
                      <>
                        <Typography.Text strong>{t('policy.groupCash')}</Typography.Text>
                        <BarisPolicy name="cashVarianceThreshold" testId="cash-variance"
                                     label={t('policy.cashVariance')} tooltip={t('policy.cashVarianceHint')}
                                     brand={rp(brandDari(editing)?.cash_variance_threshold ?? '0')}>
                          <Form.Item name="cashVarianceThreshold" label=" ">
                            <InputNumber min={0} step={1000} data-testid="pol-cash-variance" />
                          </Form.Item>
                        </BarisPolicy>
                        <BarisPolicy name="drawerOpenReviewThreshold" testId="drawer-review"
                                     label={t('policy.drawerReview')} tooltip={t('policy.drawerReviewHint')}
                                     brand={brandDari(editing)?.drawer_open_review_threshold ?? 3}>
                          <Form.Item name="drawerOpenReviewThreshold" label=" ">
                            <InputNumber min={1} max={50} data-testid="pol-drawer-review" />
                          </Form.Item>
                        </BarisPolicy>

                        <Typography.Text strong>{t('policy.groupBill')}</Typography.Text>
                        <BarisPolicy name="reprintWindowMinutes" testId="reprint-window"
                                     label={t('policy.reprintWindow')} tooltip={t('policy.reprintWindowHint')}
                                     brand={brandDari(editing)?.reprint_window_minutes ?? 60}>
                          <Form.Item name="reprintWindowMinutes" label=" ">
                            <InputNumber min={1} max={1440} data-testid="pol-reprint-window" />
                          </Form.Item>
                        </BarisPolicy>
                        <BarisPolicy name="allowReopenAfterCashTender" testId="reopen-tunai"
                                     label={t('outlets.allowReopenCash')} tooltip={t('outlets.allowReopenCashHint')}
                                     brand={ya(brandDari(editing)?.allow_reopen_after_cash_tender, t)}>
                          <Form.Item name="allowReopenAfterCashTender" label=" " valuePropName="checked">
                            <Switch data-testid="pol-reopen-tunai"
                                    checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
                          </Form.Item>
                        </BarisPolicy>

                        <Typography.Text strong>{t('policy.groupKitchen')}</Typography.Text>
                        {/*
                          ⭐⭐⭐ SATU baris untuk seluruh keputusan (0048): `0` = mati,
                          `> 0` = boleh selama N menit. Tak ada lagi pasangan saklar+angka yang
                          bisa berselisih, jadi tak ada lagi yang perlu disembunyikan/ditampilkan
                          bersyarat — barisnya selalu ada, dan artinya selalu terbaca.
                        */}
                        <BarisPolicy name="removeSentLineWindowMinutes" testId="remove-sent-window"
                                     label={t('policy.removeSent')} tooltip={t('policy.removeSentHint')}
                                     brand={menit(brandDari(editing)?.remove_sent_line_window_minutes ?? 0, t)}>
                          <Form.Item label=" ">
                            <Space align="center">
                              <Form.Item name="removeSentLineWindowMinutes" noStyle>
                                <InputNumber min={0} max={1440} data-testid="pol-remove-sent-window" />
                              </Form.Item>
                              <Form.Item noStyle shouldUpdate>
                                {(form) => (
                                  <Typography.Text type="secondary"
                                                   data-testid="pol-remove-sent-window-arti">
                                    → {menit(Number(form.getFieldValue('removeSentLineWindowMinutes') ?? 0), t)}
                                  </Typography.Text>
                                )}
                              </Form.Item>
                            </Space>
                          </Form.Item>
                        </BarisPolicy>
                        <BarisPolicy name="printZeroPriceModifiers" testId="print-zero-mod"
                                     label={t('policy.printZeroMod')} tooltip={t('policy.printZeroModHint')}
                                     brand={ya(brandDari(editing)?.print_zero_price_modifiers, t)}>
                          <Form.Item name="printZeroPriceModifiers" label=" " valuePropName="checked">
                            <Switch data-testid="pol-print-zero-mod"
                                    checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
                          </Form.Item>
                        </BarisPolicy>

                        <Typography.Text strong>{t('policy.groupGuest')}</Typography.Text>
                        <BarisPolicy name="guestCallCooldownSeconds" testId="call-cooldown"
                                     label={t('policy.callCooldown')} tooltip={t('policy.callCooldownHint')}
                                     brand={brandDari(editing)?.guest_call_cooldown_seconds ?? 30}>
                          <Form.Item name="guestCallCooldownSeconds" label=" ">
                            <InputNumber min={0} max={3600} data-testid="pol-call-cooldown" />
                          </Form.Item>
                        </BarisPolicy>
                        <BarisPolicy name="guestCallSpamThreshold" testId="call-spam"
                                     label={t('policy.callSpam')} tooltip={t('policy.callSpamHint')}
                                     brand={brandDari(editing)?.guest_call_spam_threshold ?? 3}>
                          <Form.Item name="guestCallSpamThreshold" label=" ">
                            <InputNumber min={1} max={50} data-testid="pol-call-spam" />
                          </Form.Item>
                        </BarisPolicy>
                        <BarisPolicy name="guestCallSuspendMinutes" testId="call-suspend"
                                     label={t('policy.callSuspend')} tooltip={t('policy.callSuspendHint')}
                                     brand={brandDari(editing)?.guest_call_suspend_minutes ?? 60}>
                          <Form.Item name="guestCallSuspendMinutes" label=" ">
                            <InputNumber min={1} max={1440} data-testid="pol-call-suspend" />
                          </Form.Item>
                        </BarisPolicy>
                        <BarisPolicy name="guestCallRequiresOpenShift" testId="call-needs-open"
                                     label={t('policy.callNeedsOpen')} tooltip={t('policy.callNeedsOpenHint')}
                                     brand={ya(brandDari(editing)?.guest_call_requires_open_shift, t)}>
                          <Form.Item name="guestCallRequiresOpenShift" label=" " valuePropName="checked">
                            <Switch data-testid="pol-call-needs-open"
                                    checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
                          </Form.Item>
                        </BarisPolicy>
                      </>
                    )}
                  </>
                ),
              },
              {
                key: 'fiskal',
                label: t('outlets.tabFiscal'),
                names: ['priceGroupId', 'taxExemptDeclared', 'allowReopenAfterCashTender'],
                children: (
                  <>
                    {/*
                      * ⭐⭐⭐ **Kebijakan buka-kembali bill TUNAI** — kontrol uang, dan karena itu
                      * ia tinggal di tab fiskal, bukan di tab operasi.
                      *
                      * Tunai satu-satunya tender yang bisa langsung masuk kantong. Membuka kembali
                      * bill tunai lalu menutupnya dengan tender lain — atau dengan diskon — adalah
                      * cara paling langsung menguapkan uang laci **tanpa jejak fisik**: tender
                      * non-tunai meninggalkan jejak di penyelenggara, tunai tidak.
                      *
                      * ⛔ Sebelum ini ia hanya bisa disetel lewat SQL. Kolomnya ada, servernya
                      * menegakkannya, dan tak satu pun pemilik outlet bisa menyentuhnya — sama
                      * persis dengan ambang KDS dulu (P4: perilaku = DATA, dan data yang tak
                      * terjangkau sama saja dengan terpahat).
                      *
                      * ⚠️ Bawaannya MELARANG, dan menyalakannya bukan kelonggaran kecil: kalimat
                      * peringatannya menyebut apa yang sesungguhnya diizinkan.
                      */}
                    <Form.Item
                      name="allowReopenAfterCashTender"
                      label={t('outlets.allowReopenCash')}
                      valuePropName="checked"
                      tooltip={t('outlets.allowReopenCashHint')}
                    >
                      <Switch
                        data-testid="outlet-reopen-tunai"
                        checkedChildren={t('common.on')}
                        unCheckedChildren={t('common.off')}
                      />
                    </Form.Item>
                    {/* ⚠️ Peringatan berdiri TERPISAH dari `extra`: yang di `extra` menjelaskan
                        setelannya, yang di sini menyebut RISIKONYA. */}
                    <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                           title={t('outlets.allowReopenCashWarning')}
                           data-testid="outlet-reopen-tunai-warning" />

                    {/*
                      ⭐⭐⭐ **Outlet yang memang TANPA pajak harus bisa MENYATAKANNYA.**

                      ⛔ Gerbang kesiapan menolak outlet tanpa penugasan pajak — dan itu benar:
                      "kosong diam-diam" adalah cara struk keluar tanpa PB1 tanpa ada yang
                      memutuskannya. Tapi ada outlet yang memang tak memungut apa pun, dan tanpa
                      kotak ini mereka **tak punya obat sama sekali**.

                      ⚠️ Ini PERNYATAAN, bukan kenyamanan: yang mencentangnya menyatakan outlet ini
                      tak memungut pajak — dan pernyataan itu tercatat di jejak master, bisa
                      ditanya belakangan.
                    */}
                    <Form.Item
                      name="taxExemptDeclared"
                      label={t('outlets.taxExempt')}
                      valuePropName="checked"
                      tooltip={t('outlets.taxExemptHint')}
                    >
                      <Switch data-testid="outlet-bebas-pajak"
                              checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
                    </Form.Item>

                    {/* ⭐⭐ Pindah grup harga = SELURUH harga outlet ini ikut berpindah, seketika. */}
                    <PilihGrupHarga grup={grup} editing={editing} />

                    {/*
                      * ⭐⭐ Pajak yang berlaku DI OUTLET INI — arah baca yang sebaliknya dari layar
                      * Pajak (di sana: satu kode → outlet mana saja). Pertanyaan "outlet ini kena
                      * pajak apa?" muncul saat outlet dibuka, bukan saat kode pajak disusun.
                      *
                      * ⚠️ Hanya saat MENYUNTING: outlet yang belum ada belum punya id, dan
                      * penugasan pajak butuh id. Disebut apa adanya, bukan dibiarkan kosong.
                      */}
                    {editing === null ? (
                      <Alert type="info" showIcon title={t('outlets.taxAfterSave')}
                             data-testid="outlet-pajak-nanti" />
                    ) : (
                      <>
                        <Typography.Text strong>{t('tax.codesApplied')}</Typography.Text>
                        <Alert type="info" showIcon style={{ margin: '8px 0' }}
                               title={t('tax.outletHint')} />
                        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                          {kodePajak.map((k) => {
                            const on = tugasPajak.some(
                              (x) => x.outlet_id === editing.id && x.tax_code_id === k.id,
                            );
                            return (
                              <Checkbox
                                key={k.id}
                                checked={on}
                                disabled={!boleh}
                                data-testid={`outlet-pajak-${k.code}`}
                                onChange={(e) => { ubahPajak(editing.id, k.id, e.target.checked); }}
                              >
                                {k.code} · {k.name} <Tag>{k.tax_kind}</Tag>
                              </Checkbox>
                            );
                          })}
                        </Space>
                      </>
                    )}
                  </>
                ),
              },
              {
                key: 'admin',
                label: t('outlets.tabAdmin'),
                names: ['address', 'npwp', 'isActive'],
                children: (
                  <>
                    <Form.Item name="address" label={t('outlets.address')}>
                      <Input.TextArea rows={3} maxLength={500} data-testid="outlet-alamat" />
                    </Form.Item>
                    <Form.Item name="npwp" label="NPWP">
                      <Input maxLength={30} data-testid="outlet-npwp" />
                    </Form.Item>
                    {editing !== null && (
                      <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked">
                        <Switch
                          data-testid="outlet-aktif"
                          checkedChildren={t('outlets.active')}
                          unCheckedChildren={t('outlets.inactive')}
                        />
                      </Form.Item>
                    )}
                  </>
                ),
              },
            ]}
          />
        )}
        toFormValues={(o) => ({
          ...kdsForm(setelan[o.id]),
          ...policyForm(setelan[o.id]),
          name: o.name,
          priceGroupId: o.price_group_id ?? null,
          taxExemptDeclared: setelan[o.id]?.['tax_exempt_declared'] === true,
          saleMode: o.sale_mode,
          timezone: o.timezone,
          cutover: dayjs(o.day_cutover_time, 'HH:mm:ss'),
          address: o.address ?? '',
          npwp: o.npwp ?? '',
          isActive: o.is_active,
        })}
        onCreate={async (v) => {
          await api.createOutlet({
            code: String(v['code']).toUpperCase(),
            name: v['name'],
            brandId: v['brandId'],
            saleMode: v['saleMode'],
            /* ⭐ Preset ikut: server yang menanam kapabilitasnya, di transaksi yang sama. */
            preset: v['preset'] ?? null,
            timezone: v['timezone'],
            dayCutoverTime: (v['cutover'] as dayjs.Dayjs).format('HH:mm'),
            priceGroupId: v['priceGroupId'] ?? null,
            address: String(v['address'] ?? '') === '' ? null : v['address'],
            npwp: String(v['npwp'] ?? '') === '' ? null : v['npwp'],
          });
        }}
        onUpdate={async (id, v, lama) => {
          await api.updateMaster('outlets', id, {
            name: v['name'],
            saleMode: v['saleMode'],
            timezone: v['timezone'],
            dayCutoverTime: (v['cutover'] as dayjs.Dayjs).format('HH:mm'),
            priceGroupId: v['priceGroupId'] ?? null,
            address: String(v['address'] ?? '') === '' ? null : v['address'],
            npwp: String(v['npwp'] ?? '') === '' ? null : v['npwp'],
          });
          if (v['isActive'] !== lama.is_active) {
            await api.setStatus('outlets', id, v['isActive'] === true);
          }
          await simpanSetelan(id, v);
          setSetelan(await muatSetelan());
        }}
      />
    </Space>
  );
}



/**
 * ⭐⭐⭐ **Batas jualan luring** — setelan tingkat TENANT (keputusan terbuka #4, ditutup 2026-08-25).
 *
 * Imron: *"batas jualan offline tenant setting. default 3 hari"*.
 *
 * ### Kenapa ia berdiri di layar Outlet
 * Ia bukan milik satu outlet — ⚠️ tapi di sinilah orang yang memikirkan outlet berada, dan setelan
 * yang benar secara struktural tapi tak pernah ditemukan sama saja tak ada. ⛔ Layar "Setelan
 * Tenant" tersendiri untuk SATU angka akan lebih rapi dan lebih jarang dibuka.
 *
 * ⚠️ Teksnya menyebut AKIBATNYA, bukan mekanismenya: yang dibaca pemilik adalah "berapa lama till
 * boleh jualan tanpa terhubung", bukan nama kolomnya.
 */
function BatasLuring({ boleh }: { boleh: boolean }) {
  const { t } = useTranslation();
  const [hari, setHari] = useState<number | null>(null);
  const [pesan, setPesan] = useState<string | null>(null);

  useEffect(() => {
    void api.tenantSettings()
      .then((r) => { setHari(r.offlineMaxDays); })
      .catch(() => undefined);
  }, []);

  return (
    <Card size="small" data-testid="batas-luring">
      <Space wrap align="center">
        <b>{t('outlets.offlineLimit.title')}</b>
        <InputNumber
          min={1}
          max={30}
          value={hari}
          disabled={!boleh}
          data-testid="batas-luring-hari"
          onChange={(v) => { setHari(typeof v === 'number' ? v : null); }}
        />
        <span>{t('outlets.offlineLimit.days')}</span>
        <Button
          disabled={!boleh || hari === null}
          data-testid="batas-luring-simpan"
          onClick={() => {
            if (hari === null) return;
            void api.setTenantSettings(hari)
              .then(() => { setPesan(t('common.saved')); })
              /* ⛔ Pesan server APA ADANYA — ia sudah menyebut rentangnya (MASTER-400-RANGE). */
              .catch((e: Error) => { setPesan(e.message); });
          }}
        >
          {t('common.save')}
        </Button>
        {pesan !== null && <span data-testid="batas-luring-pesan">{pesan}</span>}
      </Space>
      {/* ⚠️ Batas RISIKO, bukan batas teknis — dan itu dikatakan, supaya tak ada yang mengira
          setelan ini mengunci perangkat. */}
      <p className="hint">{t('outlets.offlineLimit.hint')}</p>
    </Card>
  );
}

/**
 * **Send Data** — memberitahu terminal bahwa ada master baru.
 *
 * ### ⭐ Bentuknya diambil dari VTEC, dan itu keputusan sadar
 * Imron (2026-08-06): *"kami setting outlet yang mau dirubah di portal, lalu tekan tombol send data
 * beserta timestamp-nya. Nanti di client muncul notifikasi untuk kasir get data."*
 *
 * ⛔ Menekan tombol ini **tidak** mengubah apa pun di kasir. Ia cuma menyalakan tombol **SYNC** di
 * layar mereka; yang menarik tetap kasir. Harga yang berganti sendiri di tengah antrean merampas
 * kendali dari orang yang berdiri di depan tamu — dan itu kelebihan alur ini, bukan kekurangannya.
 *
 * ### ⚠️ Per BRAND adalah jalur yang lebih sering benar
 * Menu & harga disusun per brand. Mengirim outlet satu per satu berarti cepat atau lambat satu
 * terlewat — dan outlet yang terlewat menjual harga lama tanpa ada yang menyadarinya.
 */
function SendData({
  brands, onSent,
}: {
  readonly brands: readonly Brand[];
  readonly onSent: () => void;
}) {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [brandId, setBrandId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  if (!boleh) return null;

  return (
    <Card size="small" data-testid="kirim-data">
      <Space wrap>
        <b>{t('outlets.sendData')}</b>
        <Select
          style={{ width: 260 }}
          placeholder={t('outlets.sendPickBrand')}
          value={brandId}
          onChange={setBrandId}
          data-testid="kirim-brand"
          options={brands.map((b) => ({ value: b.id, label: `${b.code} · ${b.name}` }))}
        />
        <Button
          type="primary"
          disabled={brandId === undefined || busy}
          data-testid="kirim-tombol"
          onClick={() => {
            if (brandId === undefined) return;
            setBusy(true);
            void api
              .publishCatalog({ brandId })
              .then((r) => {
                void message.success(t('outlets.sentTo', { n: r.outletCodes.length }));
                onSent();
              })
              .catch((e: unknown) => {
                void message.error(e instanceof Error ? e.message : String(e));
              })
              .finally(() => { setBusy(false); });
          }}
        >
          {t('outlets.sendButton')}
        </Button>
      </Space>
      {/* ⚠️ Menyebut apa yang TIDAK terjadi — kalau tidak, admin mengira kasir langsung berubah. */}
      <div style={{ marginTop: 8 }}>
        <Typography.Text type="secondary">{t('outlets.sendHint')}</Typography.Text>
      </div>
    </Card>
  );
}
